import React, { useState } from 'react';
import Select from 'react-select';
import {
  HiOutlineServer, HiOutlineRefresh,
  HiOutlineChevronDown, HiOutlineClock
} from 'react-icons/hi';
import { toast } from 'react-toastify';
import { TIME_PRESETS, toLocalDatetimeString } from '../../shared/constants';
import selectStyles from '../../shared/selectStyles';

const METRIC_COLORS = {
  'Request Count': { stroke: '#00e5ff', fill: 'rgba(0,229,255,0.12)', gradient: ['rgba(0,229,255,0.3)', 'rgba(0,229,255,0.02)'] },
  'Request Latencies (P95)': { stroke: '#f59e0b', fill: 'rgba(245,158,11,0.12)', gradient: ['rgba(245,158,11,0.3)', 'rgba(245,158,11,0.02)'] },
  'CPU Utilization': { stroke: '#10b981', fill: 'rgba(16,185,129,0.12)', gradient: ['rgba(16,185,129,0.3)', 'rgba(16,185,129,0.02)'] },
  'Memory Utilization': { stroke: '#8b5cf6', fill: 'rgba(139,92,246,0.12)', gradient: ['rgba(139,92,246,0.3)', 'rgba(139,92,246,0.02)'] },
  'Instance Count': { stroke: '#06b6d4', fill: 'rgba(6,182,212,0.12)', gradient: ['rgba(6,182,212,0.3)', 'rgba(6,182,212,0.02)'] },
  'Billable Instance Time': { stroke: '#ef4444', fill: 'rgba(239,68,68,0.12)', gradient: ['rgba(239,68,68,0.3)', 'rgba(239,68,68,0.02)'] },
};

function MetricsTab({
  product, metrics, metricsLoading, metricsTimeRange, fetchMetrics,
  metricsServiceFilter, setMetricsServiceFilter,
  metricsTimePreset, setMetricsTimePreset,
  metricsStartTime, setMetricsStartTime,
  metricsEndTime, setMetricsEndTime,
  showMetricsTimePresets, setShowMetricsTimePresets,
}) {
  const allServicesOption = { value: 'all', label: 'All Services' };
  const serviceOptions = [
    allServicesOption,
    ...(product.cloudRuns || []).map((cr, i) => ({
      value: i, label: `${cr.serviceName} (${cr.region})`, ...cr,
    })),
  ];

  const activeMetricsPresetLabel = TIME_PRESETS.find(p => p.value === metricsTimePreset)?.label || 'Select time';

  return (
    <div className="metrics-section">
      <div className="filter-toolbar">
        <div className="filter-group service-filter-group" onClick={(e) => e.stopPropagation()}>
          <HiOutlineServer size={15} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <Select
            styles={{ ...selectStyles, control: (base, state) => ({ ...selectStyles.control(base, state), minWidth: 200 }) }}
            options={serviceOptions}
            value={metricsServiceFilter ? serviceOptions.find(o => o.value === metricsServiceFilter.value) || allServicesOption : allServicesOption}
            onChange={(opt) => setMetricsServiceFilter(opt || allServicesOption)}
            placeholder="Select service..."
            isSearchable
          />
        </div>

        <div className="filter-group" onClick={(e) => e.stopPropagation()}>
          <div className="dropdown-trigger" onClick={() => setShowMetricsTimePresets(!showMetricsTimePresets)}>
            <HiOutlineClock size={15} />
            <span>{activeMetricsPresetLabel}</span>
            <HiOutlineChevronDown size={14} />
          </div>
          {showMetricsTimePresets && (
            <div className="dropdown-menu time-dropdown">
              {TIME_PRESETS.map(p => (
                <div key={p.value} className={`dropdown-item ${metricsTimePreset === p.value ? 'active' : ''}`} onClick={() => { setMetricsTimePreset(p.value); if (p.value !== 'custom') setShowMetricsTimePresets(false); }}>
                  {p.label}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="filter-spacer"></div>
        <button className="btn btn-secondary btn-sm" onClick={fetchMetrics}><HiOutlineRefresh size={14} /></button>
        {metricsTimeRange && (
          <span className="time-range-label">
            {new Date(metricsTimeRange.startTime).toLocaleString()} — {new Date(metricsTimeRange.endTime).toLocaleString()}
          </span>
        )}
      </div>

      {metricsTimePreset === 'custom' && (
        <div className="custom-time-range">
          <div className="time-input-group">
            <label>Start Time</label>
            <input type="datetime-local" value={metricsStartTime ? toLocalDatetimeString(metricsStartTime) : ''} onChange={(e) => setMetricsStartTime(e.target.value ? new Date(e.target.value).toISOString() : '')} max={toLocalDatetimeString(new Date().toISOString())} />
          </div>
          <div className="time-input-group">
            <label>End Time</label>
            <input type="datetime-local" value={metricsEndTime ? toLocalDatetimeString(metricsEndTime) : ''} onChange={(e) => setMetricsEndTime(e.target.value ? new Date(e.target.value).toISOString() : '')} max={toLocalDatetimeString(new Date().toISOString())} />
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => { if (metricsStartTime && metricsEndTime) fetchMetrics(); else toast.error('Both times required'); }}>Apply</button>
        </div>
      )}

      {metricsLoading ? (
        <div className="loading-spinner"><div className="spinner"></div></div>
      ) : !metrics || Object.keys(metrics).length === 0 ? (
        <div className="empty-state"><h3>No metrics available</h3><p>No metrics data found for the selected time range</p></div>
      ) : (
        <div className="metrics-grid">
          {Object.entries(metrics).map(([name, data]) => <MetricCard key={name} name={name} data={data} />)}
        </div>
      )}
    </div>
  );
}

function MetricCard({ name, data }) {
  const [hoverIndex, setHoverIndex] = useState(null);
  const [chartType, setChartType] = useState('bar');
  const colors = METRIC_COLORS[name] || METRIC_COLORS['Request Count'];
  const gradientId = `grad-${name.replace(/[^a-zA-Z]/g, '')}`;

  if (!data || data.totalDataPoints === 0) {
    return (
      <div className="card metric-card">
        <div className="mc-header">
          <h3>{name}</h3>
          {data?.unit && <span className="mc-unit">{data.unit}</span>}
        </div>
        <div className="metric-empty">No data points in this time range</div>
      </div>
    );
  }

  const points = data.points || [];
  const values = points.map(p => p.value).filter(v => v !== null && v !== undefined);
  const maxVal = Math.max(...values, 1e-9);
  const minVal = Math.min(...values);
  const avgVal = values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
  const latestVal = values.length > 0 ? values[values.length - 1] : 0;

  const fmtVal = (v) => {
    if (data.unit === '%') return (v * 100).toFixed(1) + '%';
    if (data.unit === 'ms') return v.toFixed(1) + ' ms';
    if (Math.abs(v) >= 1000000) return (v / 1000000).toFixed(2) + 'M';
    if (Math.abs(v) >= 1000) return (v / 1000).toFixed(1) + 'K';
    if (Number.isInteger(v)) return String(v);
    return v.toFixed(2);
  };

  const step = Math.max(1, Math.floor(points.length / 80));
  const sampled = [];
  for (let i = 0; i < points.length; i += step) sampled.push(points[i]);
  if (sampled.length === 0) sampled.push(...points.slice(0, 1));

  const W = 400, H = 100, PAD = 2;
  const chartW = W - PAD * 2, chartH = H - PAD * 2;
  const range = maxVal - minVal || 1;

  const pathPoints = sampled.map((p, i) => {
    const x = PAD + (i / Math.max(sampled.length - 1, 1)) * chartW;
    const y = PAD + chartH - ((p.value - minVal) / range) * chartH;
    return { x, y };
  });

  const linePath = pathPoints.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const areaPath = linePath + ` L${pathPoints[pathPoints.length - 1].x.toFixed(1)},${H} L${pathPoints[0].x.toFixed(1)},${H} Z`;

  const barWidth = Math.max(1, (chartW / sampled.length) * 0.7);
  const barGap = (chartW / sampled.length) * 0.15;

  const hoverPt = hoverIndex !== null && hoverIndex < sampled.length ? sampled[hoverIndex] : null;

  return (
    <div className="card metric-card" style={{ borderTop: `2px solid ${colors.stroke}` }}>
      <div className="mc-header">
        <h3>{name}</h3>
        <div className="mc-header-right">
          {data.unit && <span className="mc-unit">{data.unit}</span>}
          <div className="mc-chart-toggle">
            <button className={`mc-toggle-btn ${chartType === 'bar' ? 'active' : ''}`} onClick={() => setChartType('bar')} title="Bar Chart">
              <svg width="14" height="14" viewBox="0 0 14 14"><rect x="1" y="6" width="3" height="7" rx="0.5" fill="currentColor"/><rect x="5.5" y="3" width="3" height="10" rx="0.5" fill="currentColor"/><rect x="10" y="1" width="3" height="12" rx="0.5" fill="currentColor"/></svg>
            </button>
            <button className={`mc-toggle-btn ${chartType === 'line' ? 'active' : ''}`} onClick={() => setChartType('line')} title="Line Chart">
              <svg width="14" height="14" viewBox="0 0 14 14"><polyline points="1,11 4,6 7,8 10,3 13,5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
            </button>
          </div>
        </div>
      </div>

      <div className="mc-stats">
        <div className="mc-stat">
          <span className="mc-stat-val" style={{ color: colors.stroke }}>{fmtVal(latestVal)}</span>
          <span className="mc-stat-lbl">Latest</span>
        </div>
        <div className="mc-stat">
          <span className="mc-stat-val">{fmtVal(avgVal)}</span>
          <span className="mc-stat-lbl">Avg</span>
        </div>
        <div className="mc-stat">
          <span className="mc-stat-val">{fmtVal(maxVal)}</span>
          <span className="mc-stat-lbl">Max</span>
        </div>
        <div className="mc-stat">
          <span className="mc-stat-val">{fmtVal(minVal)}</span>
          <span className="mc-stat-lbl">Min</span>
        </div>
      </div>

      {hoverPt && (
        <div className="mc-tooltip">
          <span className="mc-tooltip-val" style={{ color: colors.stroke }}>{fmtVal(hoverPt.value)}</span>
          <span className="mc-tooltip-ts">{new Date(hoverPt.timestamp).toLocaleString()}</span>
        </div>
      )}

      <div className="mc-chart-wrap">
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="mc-svg">
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colors.gradient[0]} />
              <stop offset="100%" stopColor={colors.gradient[1]} />
            </linearGradient>
          </defs>
          {[0.25, 0.5, 0.75].map(pct => (
            <line key={pct} x1={PAD} x2={W - PAD} y1={PAD + chartH * (1 - pct)} y2={PAD + chartH * (1 - pct)} stroke="var(--chart-grid)" strokeWidth="0.5" />
          ))}

          {chartType === 'bar' ? (
            <>
              {sampled.map((p, i) => {
                const barX = PAD + (i / Math.max(sampled.length, 1)) * chartW + barGap;
                const barH = ((p.value - minVal) / range) * chartH;
                const barY = PAD + chartH - barH;
                return (
                  <g key={i} onMouseEnter={() => setHoverIndex(i)} onMouseLeave={() => setHoverIndex(null)}>
                    <rect x={barX} y={barY} width={barWidth} height={Math.max(barH, 0.5)} rx="0.5"
                      fill={hoverIndex === i ? colors.stroke : `url(#${gradientId})`}
                      stroke={colors.stroke} strokeWidth="0.3" opacity={hoverIndex === i ? 1 : 0.85} />
                    <rect x={barX} y={0} width={barWidth} height={H} fill="transparent" />
                  </g>
                );
              })}
            </>
          ) : (
            <>
              <path d={areaPath} fill={`url(#${gradientId})`} />
              <path d={linePath} fill="none" stroke={colors.stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              {pathPoints.map((p, i) => (
                <rect key={i} x={p.x - chartW / sampled.length / 2} y={0} width={chartW / sampled.length} height={H} fill="transparent"
                  onMouseEnter={() => setHoverIndex(i)} onMouseLeave={() => setHoverIndex(null)} />
              ))}
              {hoverIndex !== null && pathPoints[hoverIndex] && (
                <>
                  <line x1={pathPoints[hoverIndex].x} x2={pathPoints[hoverIndex].x} y1={PAD} y2={H} stroke="var(--alpha-white-15)" strokeWidth="0.5" strokeDasharray="3,2" />
                  <circle cx={pathPoints[hoverIndex].x} cy={pathPoints[hoverIndex].y} r="3" fill={colors.stroke} stroke="var(--bg-primary)" strokeWidth="1.5" />
                </>
              )}
            </>
          )}
        </svg>
      </div>

      <div className="mc-footer">
        <span>{points.length > 0 ? new Date(points[0].timestamp).toLocaleString() : ''}</span>
        <span>{data.totalDataPoints} pts</span>
        <span>{points.length > 0 ? new Date(points[points.length - 1].timestamp).toLocaleString() : ''}</span>
      </div>
    </div>
  );
}

export default MetricsTab;
